// The compendium (issue 1044): a search of the site's rules for anyone at the table, asked of THIS origin (the server
// reads the site; the browser never does) through `requestJson`, so `authFetch`. A hit's `url` is the site's own page
// as a path on the site; the panel puts `siteUrl()` in front of it.
import { requestJson } from './api';

export interface CompendiumHit { family: string; segment: string; slug: string; name: string; book?: string; url: string }
export interface CompendiumFamily { segment: string; label: string }
export type CompendiumAnswer = { kind: 'ok'; hits: CompendiumHit[] } | { kind: 'error' };

const enc = encodeURIComponent;
const isHit = (h: unknown): h is CompendiumHit => {
  const o = h as Record<string, unknown> | null;
  return !!o && typeof o.name === 'string' && typeof o.family === 'string' && typeof o.slug === 'string' && typeof o.url === 'string' && o.url.startsWith('/')
    && (o.book === undefined || typeof o.book === 'string');
};

/** Entries matching what was typed, across every family or the one named; nothing typed asks nothing. */
export async function searchCompendium(campaignId: string, q: string, family: string | null, fetchImpl: typeof fetch = fetch): Promise<CompendiumAnswer> {
  const text = q.trim().slice(0, 100);
  if (!text) return { kind: 'ok', hits: [] };
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium?q=${enc(text)}${family ? `&family=${enc(family)}` : ''}`, {}, fetchImpl);
  const hits = (body as { hits?: unknown } | null)?.hits;
  return status === 200 && Array.isArray(hits) ? { kind: 'ok', hits: hits.filter(isHit) } : { kind: 'error' };
}

/** The families the filter offers; none on failure (the search across all still works). */
export async function compendiumFamilies(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<CompendiumFamily[]> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/families`, {}, fetchImpl);
  const list = (body as { families?: unknown } | null)?.families;
  if (status !== 200 || !Array.isArray(list)) return [];
  return list.filter((f): f is CompendiumFamily => typeof f?.segment === 'string' && typeof f?.label === 'string');
}

// ── The default view (issue 1585, slice 1) ──
/** A default row: an entry's address, its name, its family's label, its book when known, and a right-hand detail (owner, how many, folder). */
export interface HomeRow { segment: string; slug: string; name: string; family: string; book: string | null; detail?: string }
export interface HomeGroup { key: string; title: string; rows: HomeRow[] }

const ADDRESS = /^[a-z0-9][a-z0-9._-]{0,199}$/i;
const isRow = (r: unknown): r is HomeRow => {
  const o = r as Record<string, unknown> | null;
  return !!o && typeof o.segment === 'string' && ADDRESS.test(o.segment) && typeof o.slug === 'string' && ADDRESS.test(o.slug)
    && typeof o.name === 'string' && typeof o.family === 'string' && (o.book === null || typeof o.book === 'string') && (o.detail === undefined || typeof o.detail === 'string');
};

/** The site-backed groups the panel opens on (favourites, gear), in the server's order; null when they cannot be read now. */
export async function compendiumHome(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<HomeGroup[] | null> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/home`, {}, fetchImpl);
  const groups = (body as { groups?: unknown } | null)?.groups;
  if (status !== 200 || !Array.isArray(groups)) return null;
  return groups.flatMap((g) => {
    const o = g as { key?: unknown; title?: unknown; rows?: unknown } | null;
    return o && typeof o.key === 'string' && typeof o.title === 'string' && Array.isArray(o.rows) ? [{ key: o.key, title: o.title, rows: o.rows.filter(isRow) }] : [];
  });
}

// ── An entry's hover card and the ★ (issue 1585, slice 3) ──
/** The site's hover card for one entry: its name, family, page (a path on the site), label/value rows, else a summary. */
export interface EntryCard { name: string; family: string; segment: string; slug: string; url: string; rows: Array<{ label: string; text: string }>; summary?: string }
/** What a card read gives: the card, null when the site has no such entry here, or 'error' when it cannot be read now. */
export type EntryCardAnswer = EntryCard | null | 'error';

const cards = new Map<string, Promise<EntryCardAnswer>>();
/** Forget every card read (tests; a fresh page load has none). */
export function forgetCards(): void { cards.clear(); }
const isCard = (b: unknown): b is EntryCard => {
  const o = b as Record<string, unknown> | null;
  return !!o && typeof o.name === 'string' && typeof o.family === 'string' && typeof o.segment === 'string' && typeof o.slug === 'string'
    && typeof o.url === 'string' && o.url.startsWith('/') && Array.isArray(o.rows)
    && o.rows.every((r) => typeof (r as { label?: unknown })?.label === 'string' && typeof (r as { text?: unknown })?.text === 'string')
    && (o.summary === undefined || typeof o.summary === 'string');
};

/**
 * One entry's card, asked once per segment and slug for this page: a card or a miss (404) is remembered, a failure is
 * forgotten so the next hover asks again.
 */
export function entryCard(campaignId: string, segment: string, slug: string, fetchImpl: typeof fetch = fetch): Promise<EntryCardAnswer> {
  const key = `${campaignId} ${segment}/${slug}`;
  const had = cards.get(key);
  if (had) return had;
  const asking = requestJson(`/api/tables/${enc(campaignId)}/compendium/card/${enc(segment)}/${enc(slug)}`, {}, fetchImpl).then(({ status, body }): EntryCardAnswer => {
    if (status === 200 && isCard(body)) return body;
    if (status === 404) return null;
    return 'error';
  }).catch((): EntryCardAnswer => 'error').then((got) => { if (got === 'error') cards.delete(key); return got; });
  cards.set(key, asking);
  return asking;
}

export type FavouriteAnswer = { kind: 'ok' } | { kind: 'refused'; reason: string } | { kind: 'error' };
/** Star or unstar an entry: the caller's own site favourite, through this origin (PUT; nginx allows no PATCH). */
export async function setFavourite(campaignId: string, segment: string, slug: string, starred: boolean, fetchImpl: typeof fetch = fetch): Promise<FavouriteAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/favourite`, { method: 'PUT', body: JSON.stringify({ segment, slug, starred }) }, fetchImpl);
  if (status === 204 || status === 200) return { kind: 'ok' };
  const reason = (body as { reason?: unknown } | null)?.reason;
  if (status === 422 && typeof reason === 'string') return { kind: 'refused', reason };
  return { kind: 'error' };
}

// ── Give an item or a spell to a character (issue 1055) ──
export type GiveKind = 'item' | 'spell';
export type GiveAnswer = { kind: 'ok'; said: string } | { kind: 'refused'; reason: string } | { kind: 'error' };

/** The DM's: which families' hits may be given, and as what, by segment. Empty on failure (no button is offered). */
export async function givableFamilies(campaignId: string, fetchImpl: typeof fetch = fetch): Promise<Map<string, GiveKind>> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/givable`, {}, fetchImpl);
  const list = (body as { givable?: unknown } | null)?.givable;
  const out = new Map<string, GiveKind>();
  if (status !== 200 || !Array.isArray(list)) return out;
  for (const f of list) {
    const o = f as { segment?: unknown; kind?: unknown } | null;
    if (typeof o?.segment === 'string' && (o.kind === 'item' || o.kind === 'spell')) out.set(o.segment, o.kind);
  }
  return out;
}

/** Give one hit to a character whose token is on the board the DM is looking at; the answer is the server's sentence. */
export async function giveEntry(campaignId: string, a: { characterId: string; kind: GiveKind; segment: string; slug: string; sceneId: string | null }, fetchImpl: typeof fetch = fetch): Promise<GiveAnswer> {
  const body = { characterId: a.characterId, kind: a.kind, segment: a.segment, slug: a.slug, ...(a.sceneId ? { scene_id: a.sceneId } : {}) };
  const { status, body: got } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/give`, { method: 'POST', body: JSON.stringify(body) }, fetchImpl);
  const b = (got ?? {}) as { said?: unknown; reason?: unknown };
  if (status === 201 && typeof b.said === 'string') return { kind: 'ok', said: b.said };
  if (status === 0) return { kind: 'error' };
  if (typeof b.reason === 'string') return { kind: 'refused', reason: b.reason };
  if (status === 404) return { kind: 'refused', reason: 'That character is no longer at this table.' };
  if (status === 403) return { kind: 'refused', reason: 'Only the DM can give from the compendium.' };
  return { kind: 'error' };
}

// ── A light from a spell or item (issue 1151) ──
/** An entry's light as the server reads it: radii in the system's unit (docs/MRCS/light.md), null when it sheds none. */
export interface EntryLight { name: string; segment: string; slug: string; family: string; light: { bright: number; dim: number } | null }
export type EntryLightAnswer = { kind: 'ok'; entry: EntryLight } | { kind: 'refused'; reason: string } | { kind: 'error' };

/** The spells and items (every family carrying a light) matching what was typed; nothing typed asks nothing. */
export async function searchLights(campaignId: string, q: string, fetchImpl: typeof fetch = fetch): Promise<CompendiumAnswer> {
  const text = q.trim().slice(0, 100);
  if (!text) return { kind: 'ok', hits: [] };
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/light?q=${enc(text)}`, {}, fetchImpl);
  const hits = (body as { hits?: unknown } | null)?.hits;
  return status === 200 && Array.isArray(hits) ? { kind: 'ok', hits: hits.filter(isHit) } : { kind: 'error' };
}

/** One entry's light, read by the server for this table's system. */
export async function entryLight(campaignId: string, segment: string, slug: string, fetchImpl: typeof fetch = fetch): Promise<EntryLightAnswer> {
  const { status, body } = await requestJson(`/api/tables/${enc(campaignId)}/compendium/light/${enc(segment)}/${enc(slug)}`, {}, fetchImpl);
  const b = (body ?? {}) as Partial<EntryLight> & { reason?: unknown };
  const l = b.light as { bright?: unknown; dim?: unknown } | null | undefined;
  const lightOk = l === null || (typeof l?.bright === 'number' && typeof l?.dim === 'number');
  if (status === 200 && typeof b.name === 'string' && typeof b.segment === 'string' && typeof b.slug === 'string' && lightOk) {
    return { kind: 'ok', entry: { name: b.name, segment: b.segment, slug: b.slug, family: typeof b.family === 'string' ? b.family : '', light: l as EntryLight['light'] } };
  }
  if (typeof b.reason === 'string') return { kind: 'refused', reason: b.reason };
  return { kind: 'error' };
}
